export function componentTypeAllowsAttachableType(componentType, attachableType) {
    if (!attachableType)
        return true;
    if (!componentType.allowedAttachableTypes || componentType.allowedAttachableTypes.length === 0)
        return false;
    return componentType.allowedAttachableTypes.includes(attachableType);
}
/**
 * The attachable_type a nested-only ComponentType carries. Not a real polymorphic target —
 * the row's attachable_id points at its own parent Component.
 */
export const ATTACHABLE_TYPE_FOR_CHILD_OF_COMPONENT = 'Component';
/**
 * Whether a parent Component has at least one compatible child ComponentType it could host,
 * given the parent's own ComponentType's compatibleChildTypeIds (from ComponentCompatibility
 * rows) and the full componentTypesById map.
 *
 * A candidate child type is compatible if it allows either the parent's own real attachable
 * type (mirrors the parent's underlying resource) OR the literal "Component" marker (a
 * nested-only child type, which can attach under any Component regardless of that Component's
 * own attachableType).
 */
export function componentHasCompatibleChildType(parentType, parentComponentAttachableType, componentTypesById) {
    if (!parentType || parentType.compatibleChildTypeIds.length === 0)
        return false;
    return parentType.compatibleChildTypeIds.some((childTypeId) => {
        const childType = componentTypesById.get(childTypeId);
        if (!childType)
            return false;
        return (componentTypeAllowsAttachableType(childType, parentComponentAttachableType) ||
            componentTypeAllowsAttachableType(childType, ATTACHABLE_TYPE_FOR_CHILD_OF_COMPONENT));
    });
}
/**
 * Whether `componentType` may be created under `parent`.
 *
 * A child of a Component parent is eligible on either of two independent grounds, and
 * collapsing them to one breaks the other: a nested-only type (allowedAttachableTypes
 * `["Component"]`) attaches to the parent Component itself, while an ordinary subcomponent
 * type mirrors whatever real resource the parent sits on — every pair seeded in
 * `ComponentTypes::Compatibilities::ALL` is the latter kind.
 */
export function isTypeEligibleUnderParent(componentType, parent) {
    if (!parent)
        return true;
    if (parent.kind !== 'component')
        return componentTypeAllowsAttachableType(componentType, parent.attachableType);
    return (componentTypeAllowsAttachableType(componentType, ATTACHABLE_TYPE_FOR_CHILD_OF_COMPONENT) ||
        componentTypeAllowsAttachableType(componentType, parent.attachableType));
}
/**
 * The attachable_id/attachable_type a DESCENDANT of a dragged component should inherit when
 * that component is re-parented onto `target`.
 *
 * Descendants follow the real resource the target sits on. A nested-only descendant is
 * excluded by the caller: its attachable already points at its own parent, which the move
 * does not change.
 */
export function attachableInfoForDragTarget(target) {
    return { attachableId: target.attachableId, attachableType: target.attachableType };
}
